import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, Modal, ModalBody, ModalHeader } from "reactstrap";
import ReactCrop, { centerCrop, type Crop, type PixelCrop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import "./CropPhotoModal.css";

type CropPhotoModalProps = {
  isOpen: boolean;
  imageSrc: string;
  onClose: () => void;
  onSave: (croppedDataUrl: string) => void;
};

function centerDefaultCrop(mediaWidth: number, mediaHeight: number) {
  return centerCrop(
    {
      unit: "%",
      width: 70,
      height: 70,
    },
    mediaWidth,
    mediaHeight
  );
}

function cropToDataUrl(image: HTMLImageElement, crop: PixelCrop) {
  const canvas = document.createElement("canvas");
  const scaleX = image.naturalWidth / image.width;
  const scaleY = image.naturalHeight / image.height;

  const pixelWidth = Math.floor(crop.width * scaleX);
  const pixelHeight = Math.floor(crop.height * scaleY);

  canvas.width = Math.max(1, pixelWidth);
  canvas.height = Math.max(1, pixelHeight);

  const ctx = canvas.getContext("2d");
  if (!ctx) return "";

  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";

  ctx.drawImage(
    image,
    crop.x * scaleX,
    crop.y * scaleY,
    crop.width * scaleX,
    crop.height * scaleY,
    0,
    0,
    canvas.width,
    canvas.height
  );

  return canvas.toDataURL("image/png");
}

const CropPhotoModal: React.FC<CropPhotoModalProps> = ({
  isOpen,
  imageSrc,
  onClose,
  onSave,
}) => {
  const imgRef = useRef<HTMLImageElement | null>(null);
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();

  const canSave = useMemo(() => {
    return !!completedCrop && completedCrop.width > 0 && completedCrop.height > 0;
  }, [completedCrop]);

  useEffect(() => {
    if (!isOpen) return;
    setCrop(undefined);
    setCompletedCrop(undefined);
  }, [isOpen, imageSrc]);

  const handleImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const { width, height } = e.currentTarget;
    setCrop(centerDefaultCrop(width, height));
  };

  const handleSave = () => {
    if (!imgRef.current || !completedCrop) return;
    const dataUrl = cropToDataUrl(imgRef.current, completedCrop);
    if (!dataUrl) return;
    onSave(dataUrl);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} toggle={onClose} centered size="lg" backdrop="static">
      <ModalHeader toggle={onClose}>Crop Photo</ModalHeader>
      <ModalBody>
        <div className="cpm-wrap">
          <ReactCrop
            crop={crop}
            onChange={(_, percentCrop) => setCrop(percentCrop)}
            onComplete={(c) => setCompletedCrop(c)}
            minWidth={80}
          >
            <img
              ref={imgRef}
              alt="Crop"
              src={imageSrc}
              onLoad={handleImageLoad}
              className="cpm-image"
            />
          </ReactCrop>
        </div>

        <div className="d-flex justify-content-end mt-3">
          <Button color="primary" onClick={handleSave} disabled={!canSave}>
            Save
          </Button>
        </div>
      </ModalBody>
    </Modal>
  );
};

export default CropPhotoModal;